import { epochSecondsToDate, z } from '@customerio/hydra-validation';
import type { SubscriptionTopic as SubscriptionTopicModel } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Subscription topic — sideload-only entity in this pilot. Mirrors Tag's role.
 *
 * **`id` is `string` in the entity.** The wire encodes it as a number
 * (the generated `SubscriptionTopic.id: number`) but every other entity
 * in this layer uses string ids and `Campaign.subscription_topic_id` is
 * `string` on the wire too, so we coerce at the parse boundary
 * (number → string) for one consistent type across consumers.
 */

export type SubscriptionTopic = Omit<SubscriptionTopicModel, 'id' | 'created' | 'updated'> & {
  id: string;
  created: Date;
  updated: Date;
};

const SubscriptionTopicFromApi = readSchema<SubscriptionTopic>(
  z.looseObject({
    id: z.number().transform(String),
    created: epochSecondsToDate,
    updated: epochSecondsToDate,
  }),
);

/** @public */
export const subscriptionTopic = defineEntity<SubscriptionTopic, SubscriptionTopic, void, undefined, 'workspace'>({
  name: 'subscription_topic',
  scope: 'workspace',
  pk: (s) => s.id,
  schema: { read: SubscriptionTopicFromApi },
});

export const subscriptionTopicKeys = subscriptionTopic.keys;

export function parseSubscriptionTopic(raw: SubscriptionTopicModel): SubscriptionTopic {
  return subscriptionTopic.parse(raw);
}
